// ============================================
// P3 支付线（③加购）：增值服务页
// - 目录来自 UBS addon_products（经 /pay/addons 代理）
// - 购买 → 全局支付弹窗（orderType=addon，金额以服务端目录为准）
// - 顶部展示当前生效加购权益（AI 加成/短信余额/存储扩容/去水印/3D 解锁）
// ============================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Button, Tag, Typography, Space, Empty, Spin, message, Row, Col, Input } from 'antd';
import { ShoppingCartOutlined, ReloadOutlined, ThunderboltOutlined, SearchOutlined } from '@ant-design/icons';
import { fetchPayAddons, fetchPayEntitlement, type PayAddon, type PayEntitlement } from '../api/pay';
import { usePayIntentStore } from '../components/payment/payIntent';

const { Text, Paragraph } = Typography;

const CATEGORY_LABEL: Record<string, string> = {
  ai: 'AI 能力',
  sms: '短信',
  storage: '存储',
  capability: '能力位',
  other: '其他',
};

export default function Addons() {
  const openPay = usePayIntentStore((s) => s.openPay);
  const [items, setItems] = useState<PayAddon[]>([]);
  const [ent, setEnt] = useState<PayEntitlement | null>(null);
  const [loading, setLoading] = useState(false);
  // 2026-10-10（用户要求）：增值服务列表先加入搜索
  const [keyword, setKeyword] = useState('');

  const filteredItems = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return items;
    return items.filter(
      (a) =>
        (a.name || '').toLowerCase().includes(kw) ||
        (a.description || '').toLowerCase().includes(kw) ||
        (a.code || '').toLowerCase().includes(kw) ||
        (CATEGORY_LABEL[a.category] || a.category || '').toLowerCase().includes(kw),
    );
  }, [items, keyword]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [addons, entitlement] = await Promise.all([
        fetchPayAddons(),
        fetchPayEntitlement().catch(() => null),
      ]);
      setItems(addons.items || []);
      setEnt(entitlement);
    } catch (e: any) {
      message.error(`增值服务加载失败：${e?.response?.data?.message || e?.message || ''}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const activeCodes = new Set((ent?.addons?.items || []).map((i) => i.code));

  const onBuy = (a: PayAddon) => {
    openPay(
      { orderType: 'addon', productRef: a.code, amount: Number(a.price), subject: `加购-${a.name}` },
      () => { void load(); },
    );
  };

  const summary = ent?.addons;

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>增值服务</h2>
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="按名称/说明/分类搜索"
            style={{ width: 220 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            data-testid="addons-search"
          />
          <Button icon={<ReloadOutlined />} onClick={() => void load()} data-testid="addons-refresh">刷新</Button>
        </Space>
      </Space>
      <Paragraph type="secondary" style={{ marginTop: 8 }}>
        按需购买、支付后立即生效；有效期结束或退款时自动回收。
      </Paragraph>

      {summary && (
        <div
          data-testid="addon-entitlement"
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16,
            background: '#f7f9fc', border: '1px solid #e8edf5', borderRadius: 8, padding: '10px 16px',
          }}
        >
          <Text>AI 每日加成：<Text strong data-testid="addon-ai-bonus">+{summary.aiDailyBonus} 次/天</Text></Text>
          <Text type="secondary">|</Text>
          <Text>短信余额：<Text strong data-testid="addon-sms-remaining">{summary.smsRemaining} 条</Text></Text>
          <Text type="secondary">|</Text>
          <Text>存储扩容：<Text strong>+{summary.storageBonusGb} GB</Text></Text>
          <Text type="secondary">|</Text>
          <Text>
            能力位：
            {summary.enable3D ? <Tag color="blue" style={{ marginLeft: 4 }}>3D 已解锁</Tag> : null}
            {summary.watermarkOff ? <Tag color="green" style={{ marginLeft: 4 }}>去水印生效</Tag> : null}
            {!summary.enable3D && !summary.watermarkOff ? <Text type="secondary">无</Text> : null}
          </Text>
        </div>
      )}

      <Spin spinning={loading}>
        {filteredItems.length === 0 && !loading ? (
          <Empty description={keyword ? '没有匹配的增值服务' : '暂无可购增值服务'} />
        ) : (
          <Row gutter={[16, 16]}>
            {filteredItems.map((a) => (
              <Col key={a.code} xs={24} sm={12} lg={8} xl={6}>
                <Card
                  size="small"
                  title={<Space><ThunderboltOutlined />{a.name}</Space>}
                  extra={<Tag color={activeCodes.has(a.code) ? 'green' : 'default'}>{activeCodes.has(a.code) ? '已购' : '未购买'}</Tag>}
                  data-testid={`addon-card-${a.code}`}
                >
                  <Paragraph type="secondary" style={{ minHeight: 42 }}>{a.description}</Paragraph>
                  <Space orientation="vertical" size={6} style={{ width: '100%' }}>
                    <Text strong style={{ fontSize: 18, color: '#1677ff' }}>
                      ¥{Number(a.price).toFixed(2)}
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>/{a.unitLabel}</Text>
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {CATEGORY_LABEL[a.category] || a.category} · {a.durationDays ? `${a.durationDays} 天有效` : '永久有效'}
                    </Text>
                    <Button
                      type="primary" block icon={<ShoppingCartOutlined />}
                      onClick={() => onBuy(a)}
                      data-testid={`addon-buy-${a.code}`}
                    >
                      购买
                    </Button>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Spin>
    </div>
  );
}
